import { Network } from '@spritz-finance/sdk'
import { Body, breakpoints, Button, colors, Dialog, Margin, Spacer } from '@spritz-finance/ui'
import { useRef } from 'react'
import styled from 'styled-components'
import smartPay from '../../../../../../assets/icons/smart-pay.svg'
import {
    ConfirmationDialog,
    ConfirmationDialogMethods,
} from '../../../../../../components/ConfirmationDialog'
import { FadePresence } from '../../../../../../components/FadePresence'
import { Column } from '../../../../../../components/Layout/Column'
import { Row } from '../../../../../../components/Layout/Row'
import {
    SubscriptionErrorType,
    useRetrySubscriptionChargeMutation,
} from '../../../../../../graphql/generated/graphql'
import { cssVar } from '../../../../../../theme/utils'
import { Wallet } from '../../../../../../web3/Wallet'
import { useDeleteSubscription } from '../../../../utils/useDeleteSubscription'
import { AllowanceProvider } from '../../../directPay/allowance/AllowanceProvider'
import { TokenBalanceProvider } from '../../../directPay/balance/BalanceProvider'
import { AllowanceConfiguration } from '../../../directPay/form/AllowanceConfiguration'
import { Subscription } from '../../Bills'
import { SubscriptionDetails } from './CurrentSubscription'
import { SubscriptionError } from './SubscriptionError'

const ContentContainer = styled.div`
    max-height: 80vh;
    min-height: 195px;
    padding: 16px;
    padding-top: 0px;
    overflow: auto;
    overflow-x: clip;
    max-width: 498px;
    ${breakpoints.med} {
        max-width: unset;
        padding: 16px;
    }
`
const ProcessingContainer = styled.div`
    background-color: var(--surface-3, ${colors.seaSpray[50]});
    border-radius: 10px;
    padding: ${Margin.Small};
    display: flex;
    flex-direction: column;
`

const ProcessingMessage = () => {
    return (
        <ProcessingContainer>
            <Body.Bold size={14} color={cssVar('--text-success', colors.seaSpray[75])}>
                Retrying payment!
            </Body.Bold>
            <Spacer height={8} />
            <Body.Med size={14} color={cssVar('--text-success', colors.seaSpray[75])}>
                We are attempting to retry your payment. We will let you know when it is complete or
                if there are any further issues.
            </Body.Med>
        </ProcessingContainer>
    )
}

interface ManageSubscriptionDialogProps {
    open: boolean
    onClose: () => void
    subscription: Subscription | null
}

const Logo = styled.img`
    width: 24px;
`

const ManageSubscriptionContent: React.FC<{
    subscription: Subscription
}> = ({ subscription, children }) => {
    const [retry, { loading: retrying }] = useRetrySubscriptionChargeMutation({
        variables: {
            subscriptionId: subscription.id,
        },
        update: (cache) => {
            cache.modify({
                id: cache.identify(subscription!),
                fields: {
                    processing() {
                        return true
                    },
                },
            })
        },
    })
    const canRetry = subscription.error && subscription.error.type === SubscriptionErrorType.Balance

    return (
        <ContentContainer>
            <Row align="center">
                <Wallet responsive clickable={false} />
            </Row>
            <Spacer height={24} />
            <SubscriptionDetails subscription={subscription} />
            <Spacer height={16} />
            {subscription.error && (
                <FadePresence renderKey={subscription.processing}>
                    {!subscription.processing ? (
                        <SubscriptionError subscription={subscription}>
                            {canRetry && (
                                <Row align="center">
                                    <Button.CTA
                                        onClick={() => retry()}
                                        variant="secondary"
                                        responsive
                                        loading={retrying}
                                    >
                                        Retry payment
                                    </Button.CTA>
                                </Row>
                            )}
                        </SubscriptionError>
                    ) : (
                        <ProcessingMessage />
                    )}
                </FadePresence>
            )}
            <AllowanceConfiguration />
            <Spacer height={16} />
            {children ? (
                <>
                    {children} <Spacer height={16} />
                </>
            ) : null}
        </ContentContainer>
    )
}

export const ManageSubscriptionDialog: React.VFC<ManageSubscriptionDialogProps> = ({
    open,
    onClose,
    subscription,
}) => {
    const confirmationDialog = useRef<ConfirmationDialogMethods>(null)
    const { deleteSubscription, loading } = useDeleteSubscription(subscription, onClose)
    if (!subscription) return null

    const handleCancel = () => {
        confirmationDialog.current?.confirm(async () => await deleteSubscription())
    }

    const token = {
        address: subscription.paymentToken!,
        network: subscription.network as Network,
        wallet: subscription.walletAddress!,
        symbol: subscription.tokenSymbol,
    }

    return (
        <Dialog open={open} onClose={onClose}>
            <Dialog.Content contentPadding={false} minWidth={560}>
                <Dialog.Title>
                    <Column style={{ margin: '0 16px' }} align="center">
                        <Row align="center">
                            <Logo src={smartPay} />
                            <Spacer width={8} />
                            <>Manage SMARTPay</>
                        </Row>
                    </Column>
                </Dialog.Title>
                <Spacer height={24} />
                {open && (
                    <TokenBalanceProvider token={token}>
                        <AllowanceProvider token={token}>
                            <ManageSubscriptionContent subscription={subscription}>
                                <Row align="center">
                                    <Button.CTA
                                        variant="secondary"
                                        onClick={handleCancel}
                                        loading={loading}
                                    >
                                        Cancel SMARTPay
                                    </Button.CTA>
                                    <Spacer width={24} />
                                    <Button.CTA onClick={onClose}>Done</Button.CTA>
                                </Row>
                            </ManageSubscriptionContent>
                        </AllowanceProvider>
                    </TokenBalanceProvider>
                )}
                <ConfirmationDialog ref={confirmationDialog} confirmText="Yes, I'm sure!">
                    <Body.Bold size={18}>Cancel SMARTPay</Body.Bold>
                    <Spacer height={16} />
                    <Body.Med size={14} align="center">
                        Are you sure you want to cancel this SMARTPay subscription?
                    </Body.Med>
                </ConfirmationDialog>
            </Dialog.Content>
        </Dialog>
    )
}
